import { IconName } from 'ngx-bootstrap-icons'
export interface Dictionary<T> {
    [key: string]: T
}

export enum SearchOptionType {
    daterange,
    checkboxes,
    numberrange,
    stringinput,
    multiautocomplete,
    searchnumbers,
    select,
    showoptiongroup,
    switchoptiongroup,
}

export interface TableColumnDefiniton {
    locked?: boolean
    sortable?: boolean
    visible?: boolean
    name?: string
    runningOrder?: number
    visibleIfPresent?: boolean
    visibleIfOptionGroupFilterEngaged?: boolean
    alwaysHidden?: boolean
    showInDownloads?: boolean
    classes?: string[]
}

export interface SearchOption {
    icon: IconName
    id: string
    type: SearchOptionType
    name: string
    display?: string
    displayOptions?: Dictionary<any>
    active?: boolean
    selected?: boolean
    visible?: boolean
    depends?: Dictionary<string[]>
    dependsMust?: Dictionary<string[]>
    lastResort?: boolean
    lookupOnPaste?: boolean
    tableColumn?: TableColumnDefiniton
    colour?: string,
}

export interface ShowSearchOptionGroup extends SearchOption {
    value?: string
}
export interface SwitchSearchOptionGroup extends SearchOption {
    value?: string
}

export interface CheckboxOption {
    name: string
    value?: string
    checkedDefault?: boolean
    checked?: boolean
    onlyAll?: string
    depends?: Dictionary<string[]>
    conditionalOptions?: conditionalSearchCheckboxOption
    activateDependents? : string[]
}

export interface conditionalSearchCheckboxOption  extends SearchCheckboxOption {
    showWhenOnlyChecked?: boolean
}

export interface SearchCheckboxOption extends SearchOption {
    alwaysSend?: boolean
    checkboxes: CheckboxOption[]
    numberChecked?: number
}

export interface SearchNumberRangeOption extends SearchOption {
    minValue: number
    maxValue: number
    options: {
        floor: number,
        ceil: number,
        hideLimitLabels?: boolean,
        hidePointerLabels?: boolean
    }
    prefix: string
    suffix: string
    orPercent?: boolean
    orPercentOptions?: {
        showPercent: boolean
        minValue: number,
        maxValue: number
    },
    checkboxes?: CheckboxOption[]
    numberChecked?: number
}
export interface SearchDateRangeOption extends SearchOption {
    minDate: Date
    maxDate: Date
    relativeRange?: string
}
export interface SearchStringOption extends SearchOption {
    searchString: string
    matchingOption?: string
    matchingOptionTypes?: {
        exactPhrase: boolean
        allWords: boolean
        someWords: boolean
        booleanMode: boolean
    },
    checkboxes?: CheckboxOption[]
    numberChecked?: number
}
export interface SearchNumbersOption extends SearchOption {
    numberRange: string
}

export interface SearchMultiAutoCompleteOption extends SearchOption {
    required: any
    filterby: string
    filter?: string[]
    items: {
        id: string | number
        title: string
        filterby: string
    }[]
    itemfilters?: Dictionary<string>
    searchServiceParameter?: Dictionary<any>
    searchTimer?: any
    searching?: boolean
}

export interface SearchSelectOption extends SearchOption {
    selectOptions: {
        label: string
        optionValue: string
    }[],
    selectedValue: string
}

export interface SearchOptionGroup {
    groupName: string
    visible?: boolean
    colour: string,
    depends?: Dictionary<string[]>
    dependsMust?: Dictionary<string[]>
    showInDownloads?: boolean
    options: SearchOption[]
}

export interface NamedSearchParameters {
    area?: string,
    name?: string
    id?: number
    parameters?: Dictionary<any>
    order?: {
        column: string
        direction: string
    }
    columns?: string[],
    window? : {
        start: number,
        size: number
    }
}
export interface ExportColumn {
    id: string
    name: string
    type: SearchOptionType
    selected: boolean
}

export interface ExportColumnGroup {
    groupName: string
    options: ExportColumn[]
}

export function isSearchCheckboxOption(option: SearchOption): option is SearchCheckboxOption {
    return (option as SearchCheckboxOption).type === SearchOptionType.checkboxes
}

export function isSearchDateRangeOption(option: SearchOption): option is SearchDateRangeOption {
    return (option as SearchDateRangeOption).minDate !== undefined
}

export function isSearchNumberRangeOption(option: SearchOption): option is SearchNumberRangeOption {
    return (option as SearchNumberRangeOption).minValue !== undefined
}
export function isSearchNumbersOption(option: SearchOption): option is SearchNumbersOption {
    return (option as SearchNumbersOption).numberRange !== undefined
}

export function isSearchStringOption(option: SearchOption): option is SearchStringOption {
    return (option as SearchStringOption).searchString !== undefined
}
export function isSearchMultiAutoCompleteOption(option: SearchOption): option is SearchMultiAutoCompleteOption {
    return (option as SearchMultiAutoCompleteOption).required !== undefined
}

export function isSearchSelectOption(option: SearchOption): option is SearchSelectOption {
    return (option as SearchSelectOption).selectOptions !== undefined
}

export function isSearchShowSearchOptionGroup(option: SearchOption): option is ShowSearchOptionGroup {
    return (option as ShowSearchOptionGroup).type === SearchOptionType.showoptiongroup
}

export function isSwitchSearchOptionGroup(option: SearchOption): option is SwitchSearchOptionGroup {
    return (option as SwitchSearchOptionGroup).type === SearchOptionType.switchoptiongroup
}

export function initSearchOptionGroup(groups: SearchOptionGroup[]): SearchOptionGroup[] {
    for (let group of groups) {
        group.options.forEach(option => {
            option.colour = option.colour || group.colour
            option.tableColumn = {
                locked: false,
                sortable: true,
                visible: false,
                visibleIfPresent: false,
                visibleIfOptionGroupFilterEngaged: false,
                alwaysHidden: false,
                showInDownloads: true,
                classes: [],
                ...option.tableColumn
            }
            option.active = option.active ?? false
            option.selected = option.selected ?? false
            option.visible = option.visible ?? true
            option.lastResort = option.lastResort ?? false
            option.lookupOnPaste = option.lookupOnPaste ?? false
            option.display = option.display ?? ''

            if (isSearchStringOption(option)) {
                if (option.matchingOption) {
                    option.matchingOptionTypes = option.matchingOptionTypes ?? {
                        exactPhrase: true,
                        allWords: true,
                        someWords: true,
                        booleanMode: false,
                    }
                } else {
                    option.matchingOption = 'phrase'
                }
            }

            if (isSearchNumberRangeOption(option)) {
                option.minValue = option.options?.floor ?? 1
                option.maxValue = option.options?.ceil ?? 100
                option.options = {
                    hideLimitLabels: true,
                    hidePointerLabels: true,
                    ...option.options
                }

                option.checkboxes = option.checkboxes ?? []
                option.checkboxes.forEach((checkbox => {
                    checkbox.checked = checkbox.checked ?? true
                    return checkbox
                }))
            } else if (isSearchCheckboxOption(option)) {
                option.checkboxes.forEach((checkbox => {
                    checkbox.checkedDefault = checkbox.checkedDefault ?? true
                    checkbox.checked = checkbox.checked ?? checkbox.checkedDefault
                    checkbox.onlyAll = 'only'
                    return checkbox
                }))
                option.numberChecked = option.numberChecked ?? 0
                option.alwaysSend = option.alwaysSend ?? false
                option.checkboxes.forEach( (checkbox) => {
                    checkbox.value = checkbox.value ?? checkbox.name
                    if (checkbox.conditionalOptions) {
                        checkbox.conditionalOptions.showWhenOnlyChecked = checkbox.conditionalOptions.showWhenOnlyChecked ?? false
                    }
                })
            } else if (isSearchDateRangeOption(option)) {
                option.minDate = new Date()
                option.maxDate = new Date()
            } else if (isSearchStringOption(option)) {
                option.searchString = ''
            } else if (isSearchNumbersOption(option)) {
                option.numberRange = ''
                option.active = false
            } else if (isSearchMultiAutoCompleteOption(option)) {
                option.required = []
                option.filterby = ''
                option.items = []
                option.searching = false
                option.itemfilters = {}
            }
        })
        if (! group.hasOwnProperty('visible')) {
            group.visible = true
        }
        if (! group.hasOwnProperty('showInDownloads')) {
            group.showInDownloads = true
        }
    }
    return groups
}
